/* eslint-disable no-shadow */

import { filterTicketsTree } from '@/app/modules/seating/guestOperations/Picker/domain/tickets/filterTicketsTree';
import { sortTicketsTree } from '@/app/modules/seating/guestOperations/Picker/domain/tickets/sortTicketsTree';
import { guestOperationsSeatAllocationApi } from '@/app/shared/services/apis/guestOperationsSeatAllocationApi.service';
import {
    toggleElementsOnList,
    toObjectByKey,
    uniqueValues,
} from '@fifa-bsm/bo-frontend-shared/utils';
import {
    isDistributingTicket,
    isTicketDistributed,
} from '@/app/modules/seating/guestOperations/Picker/domain/tickets/tickets';
import { GuestsVisibility } from '@/app/modules/seating/guestOperations/Picker/domain/enums/GuestsVisibility';
import { pipe } from '@fifa-bsm/bo-frontend-shared/utils';
import { filterByUserEntityAndVisibility } from '@/app/modules/guestList/EventGuestList/domain/filterByUserEntityAndVisibility';
import { omit } from '@fifa-bsm/bo-frontend-shared/utils';
import { guestListApi } from '@/app/shared/services/apis/guestListApi.service';
import { canCancelTicket } from '@/app/modules/seating/guestOperations/Picker/domain/tickets/predicates';
import { TicketListType } from '@/app/modules/seating/guestOperations/Picker/domain/enums/TicketListType';

const filtersInitialValues = {
    query: null,
    organisation: null,
    country: null,
    seatCategory: null,
    participantGroup: null,
    participantStatus: null,
    protocolHeartStatus: null,
    onlyLastMinuteChanged: false,
    onlyNotAllocated: false,
    onlyAssignedToAreaId: null,
};

const state = () => ({
    filters: { ...filtersInitialValues },
    listType: TicketListType.ALL,
    loading: false,
    itemsPage: 1,
    sortBy: null,
    items: [],
    ticketsActiveForAllocation: [],
    processedTicketIds: [],
    guestsVisibility: GuestsVisibility.ALL_GUESTS,
});

const getters = {
    guests(state) {
        return state.items.filter((guest) => {
            return guest.tickets.length > 0;
        });
    },
    tickets(state, getters, rootState, rootGetters) {
        return getters.guests.reduce((tickets, guest) => {
            const guestTickets = guest.tickets.map((ticket, index) => ({
                ...ticket,
                ticket,
                index,
                guestTicketCount: guest.tickets.length,
                guest: omit(guest, ['tickets']),
                seat: ticket.venue_seat_id
                    ? rootGetters['SeatAllocation/guestOperations/Picker/seatsMap']?.[
                          ticket.venue_seat_id
                      ]
                    : null,
            }));

            tickets.push(...guestTickets);

            return tickets;
        }, []);
    },
    ticketsMap(state, getters) {
        return toObjectByKey(getters.tickets, 'id');
    },
    ticketsAvailableForCancellation(state, getters, rootState, rootGetters) {
        return state.ticketsActiveForAllocation.filter((ticket) =>
            canCancelTicket(
                ticket,
                ticket.seat,
                rootGetters['SeatAllocation/guestOperations/Picker/eTicketingType'],
                { forbidIfCancelling: true }
            )
        );
    },
    ticketsOnWaitingList(state, getters) {
        return getters.tickets.filter((_ticket) => _ticket.on_waiting_list);
    },
    ticketsTree(state, getters) {
        let _tickets = getters.tickets;

        if (state.listType === TicketListType.WAITING_LIST) {
            _tickets = _tickets.filter((_ticket) => _ticket.on_waiting_list);
        }

        const groupedTicketsByPatronId = _tickets.reduce((acc, ticket) => {
            const key = ticket.guest.patron_id ?? ticket.guest.id;
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(ticket);
            return acc;
        }, {});

        return Object.values(groupedTicketsByPatronId).reduce((acc, tickets) => {
            const rootTicket =
                tickets.find((ticket) => ticket.guest.patron_id === null) || tickets[0];

            const restOfTicketsInGroup = tickets.filter((_ticket) => _ticket.id !== rootTicket.id);

            acc.push({
                ...rootTicket,
                nestedTickets: restOfTicketsInGroup,
            });
            return acc;
        }, []);
    },
    filteredTicketsTree(state, getters, rootState, rootGetters) {
        const userEntity = rootGetters['SeatAllocation/guestOperations/Picker/userEntity'];
        const guestsVisibility = state.guestsVisibility;

        const filteredTree = pipe(
            (ticketsTree) =>
                filterByUserEntityAndVisibility(ticketsTree, userEntity, guestsVisibility, {
                    participantSelector: (ticket) => ticket.guest,
                }),
            (ticketsTree) => filterTicketsTree(ticketsTree, state.filters)
        )(getters.ticketsTree);

        return state.sortBy ? sortTicketsTree(filteredTree, state.sortBy) : filteredTree;
    },
    notProcessedActiveTickets(state) {
        return state.ticketsActiveForAllocation.filter(
            (ticket) => !state.processedTicketIds.includes(ticket)
        );
    },
    notProcessedActiveTicketsIds(state, getters) {
        return getters.notProcessedActiveTickets.map((ticket) => ticket.id);
    },
    ticketActiveForAllocation(state) {
        return state.ticketsActiveForAllocation.filter(
            (ticket) => !state.processedTicketIds.includes(ticket.id)
        )[0];
    },
    notDistributedActiveTickets(state, getters) {
        return getters.notProcessedActiveTickets.filter(
            (ticket) => !isDistributingTicket(ticket) && !isTicketDistributed(ticket)
        );
    },
};

const actions = {
    async fetchList({ commit }, { eventId, GOSeatPlanId }) {
        commit('setLoading', true);

        const guests = await fetchAndMapGuests(eventId, GOSeatPlanId);
        Object.freeze(guests);
        for (const guest of guests) {
            Object.freeze(guest);
        }

        commit('setItems', guests);
        commit('setLoading', false);
    },
    async refreshList({ rootGetters, dispatch }) {
        await dispatch('fetchList', {
            eventId: rootGetters['SeatAllocation/guestOperations/Picker/eventId'],
            GOSeatPlanId: rootGetters['SeatAllocation/guestOperations/Picker/currentGOSeatPlan'].id,
        });
    },
    async fetchParticipantsByIdsAndUpdateItems(
        { state, getters, rootGetters, commit },
        { ticketIds, participantIds }
    ) {
        const allParticipantIds = uniqueValues([
            ...(ticketIds
                ? getters.tickets
                      .filter((ticket) => ticketIds.includes(ticket.id))
                      .map((ticket) => ticket.guest.id)
                : []),
            ...(participantIds ?? []),
        ]);
        const allExistingParticipantIds = new Map(
            Object.entries(
                getters.tickets.reduce((acc, ticket) => {
                    acc[ticket.guest.id] = true;
                    return acc;
                }, {})
            )
        );

        const participantIdsToAdd = (allParticipantIds ?? []).filter(
            (id) => !allExistingParticipantIds.has(id.toString())
        );

        const eventId = rootGetters['SeatAllocation/guestOperations/Picker/eventId'];
        const GOSeatPlanId =
            rootGetters['SeatAllocation/guestOperations/Picker/currentGOSeatPlan'].id;

        if (!GOSeatPlanId) {
            return;
        }

        const guestsById = toObjectByKey(
            await fetchAndMapGuests(eventId, GOSeatPlanId, { participantIds: allParticipantIds })
        );

        const participantIdsToRemove = allParticipantIds.filter((id) => !guestsById[id]);

        const newItems = state.items.reduce((newList, item) => {
            if (participantIdsToRemove.includes(item.id)) {
                return newList;
            }

            const newItem = guestsById[item.id];

            if (newItem) {
                Object.freeze(newItem);
                newList.push(newItem);
            } else {
                newList.push(item);
            }

            return newList;
        }, []);

        participantIdsToAdd.forEach((participantId) => {
            if (!guestsById[participantId]) {
                return;
            }

            newItems.push(guestsById[participantId]);
        });
        Object.freeze(newItems);

        commit('setItems', newItems);
    },
    async setToWaitingList({ rootState, dispatch, commit }, { ticketIds, onWaitingList }) {
        if (ticketIds.length === 0) {
            return;
        }

        const mainEventId = rootState.SeatAllocation.guestOperations.Picker.mainEvent.id;
        const eventId = rootState.SeatAllocation.guestOperations.Picker.event.id;

        await guestListApi.setToWaitingList(mainEventId, eventId, {
            ticket_ids: ticketIds,
            on_waiting_list: onWaitingList,
        });

        commit('setTicketsActiveForAllocation', []);
        await dispatch('refreshList');
    },
};

const mutations = {
    initialize(storeState) {
        Object.assign(storeState, state());
    },
    setItems(state, items) {
        state.items = items;
    },
    setListType(state, listType) {
        state.listType = listType;
    },
    setLoading(state, loading) {
        state.loading = loading;
    },
    updateSearchQuery(state, value) {
        state.ticketsActiveForAllocation = [];
        state.filters.query = value;
        state.itemsPage = 1;
    },
    updateSorting(state, value) {
        state.sortBy = value;
    },
    updateFilter(state, { name, value }) {
        state.filters = {
            ...state.filters,
            [name]: value,
        };
        state.itemsPage = 1;
    },
    clearFilters(state) {
        state.filters = { ...filtersInitialValues };
    },
    loadMoreItems(state) {
        state.itemsPage = state.itemsPage + 1;
    },
    setTicketsActiveForAllocation(state, tickets) {
        state.ticketsActiveForAllocation = tickets;
    },
    toggleTicketsActiveForAllocation(state, tickets) {
        state.ticketsActiveForAllocation = toggleElementsOnList(
            state.ticketsActiveForAllocation,
            tickets
        );
    },
    toggleTicketInActiveForAllocationList(state, ticket) {
        state.ticketsActiveForAllocation = toggleElementsOnList(state.ticketsActiveForAllocation, [
            ticket,
        ]);
    },
    removeManyTicketsFromActiveForAllocationList(state, ticketIds) {
        state.ticketsActiveForAllocation = state.ticketsActiveForAllocation.filter(
            (ticket) => !ticketIds.includes(ticket.id)
        );
    },
    setGuestsVisibility(state, visibility) {
        state.guestsVisibility = visibility;
    },
    addTicketIdsToProcessed(state, ticketIds) {
        state.processedTicketIds = uniqueValues([...state.processedTicketIds, ...ticketIds]);
    },
    removeTicketIdsFromProcessed(state, ticketIds) {
        state.processedTicketIds = state.processedTicketIds.filter(
            (ticketId) => !ticketIds.includes(ticketId)
        );
    },
    updateAccessControlStatusForSeat(state, { seatId, controlled }) {
        state.items.forEach((guest) => {
            const indexOfTicketWithSeat = guest.tickets.findIndex(
                (ticket) => ticket.venue_seat_id === seatId
            );

            if (
                indexOfTicketWithSeat === -1 ||
                !guest.tickets[indexOfTicketWithSeat].secutix_ticket
            ) {
                return;
            }

            guest.tickets[indexOfTicketWithSeat].secutix_ticket.controlled = controlled;
        });
    },
};

const fetchAndMapGuests = async (eventId, GOSeatPlanId, { participantIds } = {}) => {
    const response = await guestOperationsSeatAllocationApi.getTickets(eventId, GOSeatPlanId, {
        participant_ids: participantIds,
    });
    const guests = response.data;

    const participantIdentifiersById = toObjectByKey(response.participant_identifiers ?? []);

    return guests.map((guest) => ({
        ...guest,
        participantIdentifier: participantIdentifiersById[guest.go_participant_identifier_id],
        participantGroupIdentifier:
            participantIdentifiersById[guest.participant_group?.go_participant_identifier_id],
    }));
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
